import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Select, MenuItem, FormControl, InputLabel,
  Typography, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, Chip, Box, Checkbox, IconButton, Tooltip
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { supabase } from '../supabase';
import { t } from '../utils/strings';

const SEARCHABLE_COLUMNS = (lang: string) => lang === 'he' ? [
  { value: '_all', label: 'כל השדות' },
  { value: 'title_heb', label: 'כותרת' },
  { value: 'desc_heb', label: 'תיאור' },
  { value: 'source', label: 'מקור' },
  { value: 'place_name_heb', label: 'שם מקום' },
  { value: 'address', label: 'כתובת' },
] : [
  { value: '_all', label: 'All fields' },
  { value: 'title', label: 'Title' },
  { value: 'desc', label: 'Description' },
  { value: 'source', label: 'Source' },
  { value: 'place_name', label: 'Place Name' },
  { value: 'address', label: 'Address' },
];

const TEXT_COLUMNS = ['title', 'desc', 'source', 'title_heb', 'desc_heb', 'place_name', 'place_name_heb', 'address'];
const PAGE_SIZE = 1000;

interface MatchRow {
  id: number;
  column: string;
  columnLabel: string;
  value: string;
}

const FindReplaceWidget = ({ open, onClose, lang = 'en' }: { open: boolean, onClose: () => void, lang?: string }) => {
  const [column, setColumn] = useState('_all');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set()); // "id:column" keys
  const [loading, setLoading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [replacingRow, setReplacingRow] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: number, fail: number } | null>(null);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(0);
  const previewPageSize = 50;

  const isHebrew = (text: string) => /[\u0590-\u05FF]/.test(text);
  const rtl = lang === 'he';
  const dir = rtl ? 'rtl' : 'ltr';

  const columns = SEARCHABLE_COLUMNS(lang);
  const rowKey = (id: number, col: string) => `${id}:${col}`;

  const fetchColumn = async (col: string, search: string): Promise<MatchRow[]> => {
    const label = columns.find(c => c.value === col)?.label || col;
    let allRows: any[] = [];
    let pg = 0;
    while (true) {
      const { data, error: err } = await supabase
        .from('media_items')
        .select('id, ' + col)
        .ilike(col, '%' + search + '%')
        .range(pg * PAGE_SIZE, (pg + 1) * PAGE_SIZE - 1);
      if (err) throw err;
      allRows = allRows.concat(data || []);
      if (!data || data.length < PAGE_SIZE) break;
      pg++;
    }
    return allRows.map(row => ({ id: row.id, column: col, columnLabel: label, value: row[col] || '' }));
  };

  const handlePreview = async () => {
    if (!findText.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    setSearched(false);
    setPage(0);
    try {
      let allMatches: MatchRow[] = [];
      const cols = column === '_all' ? TEXT_COLUMNS : [column];
      for (const col of cols) {
        const rows = await fetchColumn(col, findText);
        allMatches = allMatches.concat(rows);
      }
      setMatches(allMatches);
      setSearched(true);
      setSelected(new Set(allMatches.map(r => rowKey(r.id, r.column))));
    } catch (err: any) {
      setError(err.message || 'Preview failed');
      setMatches([]);
    }
    setLoading(false);
  };

  const handleToggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    const pageKeys = pagedMatches.map(r => rowKey(r.id, r.column));
    setSelected(prev => { const next = new Set(prev); pageKeys.forEach(k => next.add(k)); return next; });
  };

  const handleDeselectAll = () => {
    const pageKeys = new Set(pagedMatches.map(r => rowKey(r.id, r.column)));
    setSelected(prev => { const next = new Set(prev); pageKeys.forEach(k => next.delete(k)); return next; });
  };

  const replaceRow = async (match: MatchRow): Promise<boolean> => {
    const newVal = match.value.split(findText).join(replaceText);
    const { error: err } = await supabase
      .from('media_items')
      .update({ [match.column]: newVal })
      .eq('id', match.id);
    return !err;
  };

  const handleReplaceOne = async (match: MatchRow) => {
    const key = rowKey(match.id, match.column);
    setReplacingRow(key);
    const ok = await replaceRow(match);
    if (ok) {
      setMatches(prev => prev.filter(r => rowKey(r.id, r.column) !== key));
      setSelected(prev => { const next = new Set(prev); next.delete(key); return next; });
    } else {
      setError(`Failed to update row ${match.id}`);
    }
    setReplacingRow(null);
  };

  const handleReplaceSelected = async () => {
    if (selected.size === 0) return;
    const selectedMatches = matches.filter(r => selected.has(rowKey(r.id, r.column)));
    if (!window.confirm(t('confirmReplace', lang, { find: findText, replace: replaceText, count: selectedMatches.length }))) return;
    setReplacing(true);
    setError('');
    setResult(null);
    let ok = 0, fail = 0;
    const doneKeys = new Set<string>();
    for (const match of selectedMatches) {
      const success = await replaceRow(match);
      if (success) { ok++; doneKeys.add(rowKey(match.id, match.column)); }
      else fail++;
    }
    setResult({ ok, fail });
    setMatches(prev => prev.filter(r => !doneKeys.has(rowKey(r.id, r.column))));
    setSelected(prev => { const next = new Set(prev); doneKeys.forEach(k => next.delete(k)); return next; });
    setReplacing(false);
  };

  const handleClose = () => {
    setMatches([]);
    setSelected(new Set());
    setResult(null);
    setError('');
    setSearched(false);
    setPage(0);
    onClose();
  };

  const highlightMatch = (text: string) => {
    if (!text || !findText) return text;
    const escaped = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === findText.toLowerCase()
        ? <span key={i} style={{ backgroundColor: '#fff176', fontWeight: 'bold' }}>{part}</span>
        : part
    );
  };

  const totalPages = Math.max(1, Math.ceil(matches.length / previewPageSize));
  const pagedMatches = matches.slice(page * previewPageSize, (page + 1) * previewPageSize);
  const selectedOnPage = pagedMatches.filter(r => selected.has(rowKey(r.id, r.column))).length;

  const remainingText = matches.length > 0
    ? t('stillMatching', lang, { count: matches.length })
    : t('noMatchesRemaining', lang);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <div dir={dir}>
      <DialogTitle>{t('findReplaceTitle', lang)}</DialogTitle>
      <DialogContent>
        <div dir={dir} style={{ display: 'flex', gap: '16px', marginTop: '8px', marginBottom: '16px', alignItems: 'flex-end' }}>
          <FormControl size="small" dir={dir} sx={{ minWidth: 160 }}>
            <Select value={column}
              onChange={(e) => { setColumn(e.target.value); setMatches([]); setResult(null); setSearched(false); }}
              MenuProps={{ PaperProps: { dir: dir } }}>
              {columns.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label={t('find', lang)} value={findText}
            onChange={(e) => { setFindText(e.target.value); setMatches([]); setResult(null); setSearched(false); }}
            sx={{ flex: 1, ...(rtl && {
              '& .MuiInputLabel-root': { right: 28, left: 'unset', transformOrigin: 'top right' },
              '& .MuiInputLabel-shrink': { right: 28, left: 'unset' },
              '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
            }) }}
            onKeyDown={(e) => { if (e.key === 'Enter' && findText.trim() && !loading) handlePreview(); }}
            inputProps={{ dir: rtl ? 'rtl' : 'ltr', style: { textAlign: rtl ? 'right' : 'left' } }} />
          <TextField size="small" label={t('replaceWith', lang)} value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && findText.trim() && !loading) handlePreview(); }}
            sx={{ flex: 1, ...(rtl && {
              '& .MuiInputLabel-root': { right: 28, left: 'unset', transformOrigin: 'top right' },
              '& .MuiInputLabel-shrink': { right: 28, left: 'unset' },
              '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
            }) }}
            inputProps={{ dir: rtl ? 'rtl' : 'ltr', style: { textAlign: rtl ? 'right' : 'left' } }} />
          <Button variant="outlined" onClick={handlePreview} disabled={!findText.trim() || loading}
            {...(rtl
              ? { endIcon: loading ? <CircularProgress size={16} /> : null }
              : { startIcon: loading ? <CircularProgress size={16} /> : null }
            )}>
            {t('search', lang)}
          </Button>
        </div>

        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
        {result && (
          <Alert severity={result.fail ? 'warning' : 'success'} sx={{ mb: 1 }} onClose={() => setResult(null)}>
            {t('replacedRows', lang, {
              ok: result.ok,
              fail: result.fail ? t('failedRows', lang, { count: result.fail }) : '',
              remaining: remainingText,
            })}
          </Alert>
        )}

        {searched && matches.length === 0 && !loading && !result && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {t('noMatchesFound', lang, {
              text: findText,
              inField: column !== '_all' ? t('inField', lang, { field: columns.find(c => c.value === column)?.label }) : '',
            })}
          </Alert>
        )}

        {matches.length > 0 && (
          <>
            <div dir={dir} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <Chip label={t('matchesCount', lang, { count: matches.length })} color="primary" size="small" />
              <Chip label={t('selectedCount', lang, { count: selected.size })} color="secondary" size="small" variant="outlined" />
              <Button size="small" onClick={handleSelectAll}>{t('selectPage', lang)}</Button>
              <Button size="small" onClick={handleDeselectAll}>{t('deselectPage', lang)}</Button>
              <Button size="small" onClick={() => setSelected(new Set(matches.map(r => rowKey(r.id, r.column))))}>{t('selectAll', lang)}</Button>
              <Button size="small" onClick={() => setSelected(new Set())}>{t('deselectAll', lang)}</Button>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', marginInlineStart: 'auto' }}>
                  <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>{t('prev', lang)}</Button>
                  <Typography variant="caption" sx={{ mx: 1 }}>{rtl ? `${totalPages}/${page + 1}` : `${page + 1}/${totalPages}`}</Typography>
                  <Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>{t('next', lang)}</Button>
                </div>
              )}
            </div>
            <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { fontSize: 12, py: 0.3 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 40 }}>
                      <Checkbox
                        size="small"
                        checked={selectedOnPage === pagedMatches.length && pagedMatches.length > 0}
                        indeterminate={selectedOnPage > 0 && selectedOnPage < pagedMatches.length}
                        onChange={() => selectedOnPage === pagedMatches.length ? handleDeselectAll() : handleSelectAll()}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 55, textAlign: rtl ? 'right' : 'left' }}>{rtl ? 'מזהה' : 'ID'}</TableCell>
                    {column === '_all' && <TableCell sx={{ width: 100, textAlign: rtl ? 'right' : 'left' }}>{t('field', lang)}</TableCell>}
                    <TableCell sx={{ textAlign: rtl ? 'right' : 'left' }}>{t('value', lang)}</TableCell>
                    <TableCell sx={{ width: 40 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedMatches.map(row => {
                    const key = rowKey(row.id, row.column);
                    return (
                      <TableRow key={key} hover sx={{ opacity: selected.has(key) ? 1 : 0.5 }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selected.has(key)} onChange={() => handleToggle(key)} />
                        </TableCell>
                        <TableCell sx={{ textAlign: rtl ? 'right' : 'left' }}>{row.id}</TableCell>
                        {column === '_all' && <TableCell sx={{ textAlign: rtl ? 'right' : 'left' }}><Chip label={row.columnLabel} size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} /></TableCell>}
                        <TableCell sx={{ direction: isHebrew(row.value) ? 'rtl' : 'ltr', textAlign: rtl ? 'right' : 'left', maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {highlightMatch(row.value)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t('replaceThisRow', lang)}>
                            <IconButton
                              size="small"
                              onClick={() => handleReplaceOne(row)}
                              disabled={!replaceText.trim() || findText === replaceText || replacingRow === key}
                            >
                              {replacingRow === key ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('close', lang)}</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleReplaceSelected}
          disabled={!replaceText.trim() || findText === replaceText || replacing || selected.size === 0}
          startIcon={replacing ? <CircularProgress size={16} /> : null}
        >
          {t('replaceSelected', lang, { count: selected.size })}
        </Button>
      </DialogActions>
      </div>
    </Dialog>
  );
};

export default FindReplaceWidget;
